import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import translate from "@insite/client-framework/Translate";
import { VariantTraitModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { css } from "styled-components";

/* Custom Code - Start */
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import useSwatchSelectedAccessibleText from "@store/Framework/Common/Hooks/useSwatchSelectedAccessibleText";
import { swatchListItemStyles, swatchListStyles } from "@store/Themes/common/mixins/swatch";
import { swatchProps } from "@store/Themes/common/props/swatch";
/* Custom Code - End */

/* Store Custom Code - Start */
import getColor from "@insite/mobius/utilities/getColor";
import mergeProps from "@store/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@store/Framework/Common/Utilities/unitHelper";
import { TraitValueModel } from "@store/Framework/Types/ApiModels";
/* Store Custom Code - End */

interface Props {
    variantSelection: SafeDictionary<string>;
    variantTrait: VariantTraitModel;
    updateVariantSelection: (traitValueId: string, variantTraitId: string) => void;
}

export interface ProductDetailsVariantButtonStyles {
    wrapper?: InjectableCss;
    swatchHeader?: InjectableCss;
    button?: ButtonPresentationProps;
    labelText?: TypographyPresentationProps;
    link?: LinkPresentationProps;
    // Custom Code
    swatchList?: InjectableCss;
    swatchListItem?: InjectableCss;
    selectedButton?: ButtonPresentationProps;
    unselectedButton?: ButtonPresentationProps;
}

export const variantButtonsStyles: ProductDetailsVariantButtonStyles = {
    wrapper: swatchProps.wrapper,
    labelText: swatchProps.label,
    swatchList: { css: swatchListStyles() },
    swatchListItem: { css: swatchListItemStyles() },
    selectedButton: { variant: "selected" as any },
    unselectedButton: { variant: "unselected" as any },
};

const styles = variantButtonsStyles;

const ProductDetailsVariantButton = ({ variantSelection, updateVariantSelection, variantTrait }: Props) => {
    const swatchSelectedAccessibleText = useSwatchSelectedAccessibleText(variantTrait); // Custom Code
    /* Store Custom Code - Start */
    const generateButtonStyles = (backgroundColor: string, color: string) => ({
        selectedButton: {
            variant: "selected" as any,
            mergeCss: true,
            css: css`
                border-width: 3px;
                background-color: ${getColor(backgroundColor)};
                color: ${getColor(color)};
            `,
        },
        unselectedButton: {
            variant: "unselected" as any,
            mergeCss: true,
            css: css`
                background-color: ${getColor(backgroundColor)};
                color: ${getColor(color)};
                border-color: ${getColor(color)};
            `,
        },
    });

    const stylesWithStock = (traitValue: TraitValueModel) => {
        let stockStyle = {};
        if (!traitValue.isDisabled) {
            if (traitValue.isDiscontinued) {
                stockStyle = generateButtonStyles("danger.light", "danger");
            } else if (traitValue.isOrderAsNeeded || traitValue.isDeactivated) {
                stockStyle = generateButtonStyles("warning.light", "warning");
            } else if (traitValue.isStock) {
                stockStyle = generateButtonStyles("success.light", "success");
            }
        }
        return mergeProps<ProductDetailsVariantButtonStyles>(styles, stockStyle);
    };

    const extractNumber = (str: string) => {
        if (/^-?\d+(\.\d+)?$/.test(str) === false) {
            return null;
        }
        const matches = str.match(/\d+/);
        return matches ? parseInt(matches[0], 10) : null;
    };

    // Sort traits that contain a number otherwise fallback to sortorder
    const compareStyleTraitOrder = (first: TraitValueModel, second: TraitValueModel) => {
        const numA = extractNumber(first.valueDisplay);
        const numB = extractNumber(second.valueDisplay);
        if (numA !== null && numB !== null) {
            return numA - numB;
        }
        if (numA !== null && numB) {
            return -1;
        }
        if (numA && numB !== null) {
            return 1;
        }
        if (first.sortOrder - second.sortOrder === 0) {
            return first.valueDisplay > second.valueDisplay ? 1 : -1;
        }

        return first.sortOrder - second.sortOrder;
    };
    /* Store Custom Code - End */

    return (
        <StyledWrapper {...styles.wrapper} key={variantTrait.id}>
            <StyledWrapper {...styles.swatchHeader}>
                <Typography {...styles.labelText}>
                    {variantTrait.nameDisplay}
                    {/* Custom Code - Start */}
                    {swatchSelectedAccessibleText && (
                        <VisuallyHidden>{` ${swatchSelectedAccessibleText}`}</VisuallyHidden>
                    )}
                    {/* Custom Code - End */}
                </Typography>
                {/* Removed - Clear Selection */}
            </StyledWrapper>
            {/* Custom Code - Start */}
            <StyledWrapper {...styles.swatchList} as="ul" role="radiogroup" aria-label={variantTrait.nameDisplay}>
                {variantTrait.traitValues
                    ?.sort(compareStyleTraitOrder) /* Store Custom Code */
                    .map((traitValue: TraitValueModel /* Store Custom Code */) => {
                        const isSelected = variantSelection[variantTrait.id] === traitValue.id;

                        return (
                            <StyledWrapper as="li" {...styles.swatchListItem} key={`${traitValue.id}`}>
                                <Button
                                    /* Store Custom Code - Start */
                                    {...(isSelected
                                        ? stylesWithStock(traitValue).selectedButton
                                        : stylesWithStock(traitValue).unselectedButton)}
                                    /* Store Custom Code - End */
                                    value={`${traitValue.id}`}
                                    disabled={traitValue.isDisabled}
                                    onClick={event =>
                                        updateVariantSelection(
                                            isSelected ? "" : event.currentTarget.value,
                                            variantTrait.id,
                                        )
                                    }
                                    aria-checked={isSelected}
                                    role="radio"
                                >
                                    {traitValue.valueDisplay}
                                </Button>
                            </StyledWrapper>
                        );
                    })}
            </StyledWrapper>
            {/* Custom Code - End */}
        </StyledWrapper>
    );
};

export default ProductDetailsVariantButton;
